// Scrap Quote Generator — imperative API client.
//
// Pattern mirrors api/bookings.ts: thin async wrappers over apiClient
// for use in single-shot click → result UI flows (the QuotePage two-step
// form lives outside React Query). Quotes are ephemeral so there's no
// list / detail GET to cache.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  GenerateQuoteInput,
  QuoteResponse,
  SendQuoteResult,
} from '../types/api';

/**
 * Generate a new quote. Server runs DVLA + DVSA MOT lookups, resolves
 * the vehicle weight from the seed table (or a class-based fallback),
 * applies the per-tonne price (override > company default), persists a
 * 14-day-TTL row, and returns the full quote payload.
 */
export async function generateQuote(
  payload: GenerateQuoteInput,
): Promise<QuoteResponse> {
  const res = await apiClient.post<{ quote: QuoteResponse }>(
    endpoints.quotes.generate,
    payload,
  );
  return res.data.quote;
}

/**
 * Send a previously-generated quote to its customer_email via the
 * tenant's `quote_send` template. Server returns 202; the result flag
 * is `email_queued: false` when the template is missing or the row has
 * no customer_email — the UI surfaces that as a warning toast.
 */
export async function sendQuote(uuid: string): Promise<SendQuoteResult> {
  const res = await apiClient.post<SendQuoteResult>(
    endpoints.quotes.send(uuid),
  );
  return res.data;
}
